'use client';


import { AreaScore } from './DiagnosticFormInteractive';

interface StepAreaAssessmentProps {
  data: AreaScore;
  onChange: (data: AreaScore) => void;
}

type ScoreValue = 0 | 1 | 2 | 3;

interface AreaQuestion {
  id: keyof AreaScore;
  title: string;
  questions: string[];
}

const AREAS: AreaQuestion[] = [
  {
    id: 'adminOrg',
    title: 'Organização Administrativa',
    questions: [
      'Os processos administrativos estão documentados e são seguidos de forma consistente?',
      'O arquivo documental está organizado e é facilmente acessível?',
      'Existe um sistema claro de gestão de correspondência e comunicações?',
    ],
  },
  {
    id: 'financialMgmt',
    title: 'Gestão Financeira',
    questions: [
      'O orçamento anual é elaborado com base em dados históricos e projeções realistas?',
      'A execução orçamental é monitorizada regularmente com relatórios periódicos?',
      'Os processos de prestação de contas cumprem os prazos legais sem dificuldades?',
    ],
  },
  {
    id: 'humanResources',
    title: 'Recursos Humanos',
    questions: [
      'Os contratos e vínculos laborais estão atualizados e em conformidade legal?',
      'Existe um plano de formação e desenvolvimento para os colaboradores?',
      'O processo de avaliação de desempenho é realizado regularmente?',
    ],
  },
  {
    id: 'publicProcurement',
    title: 'Contratação Pública',
    questions: [
      'Os processos de aquisição seguem os procedimentos legais do Código dos Contratos Públicos?',
      'Existe um registo atualizado de todos os contratos e fornecedores?',
      'As consultas ao mercado e ajustes diretos são devidamente documentados?',
    ],
  },
];

const SCORE_OPTIONS: {
  value: ScoreValue;
  label: string;
  shortLabel: string;
  color: string;
  bgColor: string;
  borderColor: string;
}[] = [
  { value: 1, label: 'Sim — Totalmente implementado', shortLabel: 'Sim', color: '#2E7D32', bgColor: '#F1F8F1', borderColor: '#2E7D32' },
  { value: 2, label: 'Parcialmente — Em desenvolvimento', shortLabel: 'Parcialmente', color: '#F57C00', bgColor: '#FFF8F0', borderColor: '#F57C00' },
  { value: 3, label: 'Não — Necessita implementação', shortLabel: 'Não', color: '#C62828', bgColor: '#FFF0F0', borderColor: '#C62828' },
];

export default function StepAreaAssessment({ data, onChange }: StepAreaAssessmentProps) {
  const handleScore = (areaId: keyof AreaScore, value: ScoreValue) => {
    onChange({ ...data, [areaId]: value });
  };

  const completedCount = Object.values(data).filter((v) => v > 0).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-10 h-10 flex items-center justify-center flex-shrink-0"
            style={{ borderRadius: '5px', backgroundColor: '#E0F2F1', color: '#009688' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
              <rect x="9" y="3" width="6" height="4" rx="1"/>
              <line x1="9" y1="12" x2="15" y2="12"/>
              <line x1="9" y1="16" x2="12" y2="16"/>
            </svg>
          </div>
          <div className="flex-1">
            <h2
              className="text-gray-900 mb-1"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.4rem', fontWeight: 700 }}
            >
              Avaliação por Área Funcional
            </h2>
            <p
              className="text-gray-500 text-sm leading-relaxed"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Para cada área, indique o nível de implementação atual na sua Junta de Freguesia.
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <span className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '1.5rem', color: '#009688' }}>{completedCount}</span>
            <span className="block text-gray-400" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem' }}>de 4 áreas</span>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-5 pt-4" style={{ borderTop: '1px solid #F0F0F0' }}>
          {SCORE_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5"
                style={{ backgroundColor: opt.color, borderRadius: '2px' }}
              />
              <span className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>{opt.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Area Cards */}
      {AREAS.map((area) => {
        const currentScore = data[area.id] as ScoreValue;
        const isAnswered = currentScore > 0;
        const answeredOpt = SCORE_OPTIONS.find((o) => o.value === currentScore);

        return (
          <div
            key={area.id}
            className="bg-white p-7 transition-all duration-200"
            style={{
              borderRadius: '5px',
              border: isAnswered && answeredOpt ? `2px solid ${answeredOpt.borderColor}30` : '2px solid #F0F0F0',
              boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
            }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                style={{ borderRadius: '5px', backgroundColor: '#E0F2F1', color: '#009688' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <line x1="9" y1="9" x2="15" y2="9"/>
                  <line x1="9" y1="13" x2="15" y2="13"/>
                  <line x1="9" y1="17" x2="12" y2="17"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3
                  className="text-gray-900 font-semibold"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.05rem', fontWeight: 700 }}
                >
                  {area.title}
                </h3>
              </div>
              {isAnswered && answeredOpt && (
                <div
                  className="px-3 py-1 text-xs font-semibold"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    backgroundColor: answeredOpt.bgColor,
                    color: answeredOpt.color,
                    border: `1px solid ${answeredOpt.borderColor}40`,
                    borderRadius: '5px',
                  }}
                >
                  {answeredOpt.shortLabel}
                </div>
              )}
            </div>

            {/* Questions */}
            <div
              className="mb-5 space-y-2 pl-3"
              style={{ borderLeft: '2px solid #E0F2F1' }}
            >
              {area.questions.map((q, idx) => (
                <p key={idx} className="text-gray-400 text-xs leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {q}
                </p>
              ))}
            </div>

            {/* Score selection */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-3" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                Avaliação geral desta área: <span className="text-error">*</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SCORE_OPTIONS.map((opt) => {
                  const selected = currentScore === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleScore(area.id, opt.value)}
                      className="flex items-center gap-3 p-3 text-left transition-all duration-200 min-h-[44px]"
                      style={{
                        borderRadius: '5px',
                        border: selected ? `2px solid ${opt.borderColor}` : '2px solid #F0F0F0',
                        backgroundColor: selected ? opt.bgColor : 'white',
                      }}
                      aria-pressed={selected}
                    >
                      <div
                        className="w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200"
                        style={{
                          borderRadius: '50%',
                          backgroundColor: selected ? opt.color : 'transparent',
                          borderColor: selected ? opt.color : '#D0D0D0',
                        }}
                      >
                        {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                      <span
                        className="text-sm font-semibold"
                        style={{ fontFamily: 'DM Sans, sans-serif', color: selected ? opt.color : '#1A1A1A' }}
                      >
                        {opt.shortLabel}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}